import { cn } from 'cn'
import { ChevronLeft } from 'lucide-react'
import type { ReactNode } from 'react'

type AppHeaderVariant = 'default' | 'primary'

interface AppHeaderProps {
  title: string
  onBack?: () => void
  actionLabel?: string
  /** Icon action (rendered as a 44px icon button) — takes precedence over actionLabel. */
  actionIcon?: ReactNode
  actionAriaLabel?: string
  onAction?: () => void
  actionDestructive?: boolean
  /**
   * Several trailing controls instead of one — e.g. the Stocktake Progress tab's
   * funnel + magnifier pair. Takes precedence over `actionIcon`/`actionLabel`,
   * and owns its own aria-labels and coach-mark anchors, so `actionAriaLabel`
   * and `dataTour` are ignored when it is set.
   */
  actions?: ReactNode
  /** "default" = dark text on card; "primary" = white text on brand blue. */
  variant?: AppHeaderVariant
  className?: string
  /** Override classes on the title <h1>. */
  titleClassName?: string
  /** Coach-mark anchor applied to the action button. */
  readonly dataTour?: string
}

function AppHeader({
  title,
  onBack,
  actionLabel,
  actionIcon,
  actionAriaLabel,
  onAction,
  actionDestructive = false,
  actions,
  variant = 'default',
  className,
  titleClassName,
  dataTour,
}: Readonly<AppHeaderProps>) {
  const isPrimary = variant === 'primary'

  let actionTextClass = 'text-primary hover:text-primary/80'
  if (isPrimary) actionTextClass = 'text-primary-foreground hover:text-primary-foreground/80'
  else if (actionDestructive) actionTextClass = 'text-destructive'

  return (
    <header
      className={cn(
        'sticky top-0 z-10 w-full',
        isPrimary
          ? 'bg-primary text-primary-foreground'
          : 'bg-card text-foreground border-b border-border',
        className,
      )}
    >
      {/* Content row sits below the status-bar area. */}
      <div className="flex h-15 items-center px-4">
        <div className="flex min-w-[44px] items-center">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              data-app-back
              className={cn(
                'flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors',
                isPrimary
                  ? 'text-primary-foreground hover:bg-white/15'
                  : 'text-foreground hover:bg-accent',
              )}
              aria-label="Go back"
            >
              <ChevronLeft className="size-5" />
            </button>
          )}
        </div>

        <h1
          className={cn(
            'flex-1 truncate px-2 text-center text-base font-semibold',
            isPrimary ? 'text-primary-foreground' : 'text-foreground',
            titleClassName,
          )}
        >
          {title}
        </h1>

        <div className="flex min-w-[44px] items-center justify-end">
          {actions}
          {!actions && actionIcon && onAction ? (
            <button
              type="button"
              onClick={onAction}
              aria-label={actionAriaLabel ?? 'Action'}
              data-tour={dataTour}
              className={cn(
                'flex h-[44px] w-[44px] items-center justify-center rounded-full transition-colors',
                isPrimary
                  ? 'text-primary-foreground hover:bg-white/15'
                  : 'text-foreground hover:bg-accent',
              )}
            >
              {actionIcon}
            </button>
          ) : (
            !actions &&
            actionLabel &&
            onAction && (
              <button
                type="button"
                onClick={onAction}
                data-tour={dataTour}
                className={cn('text-sm font-medium transition-colors', actionTextClass)}
              >
                {actionLabel}
              </button>
            )
          )}
        </div>
      </div>
    </header>
  )
}

export { AppHeader, type AppHeaderVariant }
